import { css } from '@emotion/react'
import { useModal } from 'components/modal/Modal'
import { BREAKPOINTS, pxToRem } from 'utils/styled'
import { ReactElement, ReactNode } from 'react'
import {
  SButtonsContainer,
  SEmailModal,
  SPreviewContainer,
  SText,
  STitle,
} from './EmailModal.styled'
import { useForm } from 'react-hook-form'
import { Fieldset } from 'components/fieldset/Fieldset'
import { FormFieldset } from 'components/FormButton'
import { useMyContactsList } from 'api/goodtrust/contact'
import { useUserMe } from 'api/goodtrust/user'
import { useAuth } from 'utils/auth/hooks/useAuth'
import { handleAndToastError } from 'components/Toast'
import { BlockSpinner } from 'components/spinner/Spinner'
import { Button } from 'components/button/Button'
import { InterpolationPrimitive } from '@emotion/serialize'
import { useFieldValidation } from 'utils/i18n/useFieldValidation'
import { useDynamicTranslations } from 'utils/i18n/useDynamicTranslations'

export type EmailModalFields = {
  recipient_email: string
  name?: string
  sender_email?: string
}

const EmailModal = (props: {
  title?: ReactNode
  text?: ReactNode
  preview?: ReactElement
  previewCss?: InterpolationPrimitive
  onSend?: (payload: EmailModalFields) => void
  defaultEmail?: string
  onClose: () => void
  className?: string
}) => {
  const { t, isLoading } = useDynamicTranslations('future_message')
  const validation = useFieldValidation()
  const form = useForm<EmailModalFields>({
    defaultValues: props?.defaultEmail ? { sender_email: props.defaultEmail } : undefined,
  })

  const { isLogged } = useAuth()
  const { data, isValidating } = useUserMe()
  const hasName = data?.json?.first_name || data?.json?.last_name
  const myContacts = useMyContactsList(99)

  const handleSubmit = async (payload: EmailModalFields) => {
    try {
      await props.onSend?.(payload)
      props.onClose()
    } catch (err) {
      handleAndToastError(err, 'failed_to_send_email')
    }
  }

  if (isLoading) return <BlockSpinner />
  return (
    <SEmailModal className={props.className}>
      {props?.preview && (
        <SPreviewContainer css={props.previewCss}>{props.preview}</SPreviewContainer>
      )}
      <div>
        <STitle>{props?.title || t('future_message.email_modal.title')}</STitle>
        {props?.text && <SText>{props.text}</SText>}
      </div>
      {isLogged && !data && isValidating ? (
        <BlockSpinner />
      ) : (
        <form noValidate onSubmit={form.handleSubmit(handleSubmit)} css={{ order: 99 }}>
          <FormFieldset form={form}>
            {(!isLogged || !hasName) && (
              <FormFieldset form={form}>
                <Fieldset>
                  <input
                    {...form.register('name', validation.required())}
                    type="text"
                    placeholder="&nbsp;"
                  />
                  <label>{t('future_message.email_modal.name_label')}</label>
                </Fieldset>
              </FormFieldset>
            )}

            {!isLogged && (
              <FormFieldset form={form}>
                <Fieldset>
                  <input
                    {...form.register('sender_email', {
                      ...validation.required(),
                      ...validation.email(),
                    })}
                    type="email"
                    placeholder="&nbsp;"
                  />
                  <label>{t('future_message.email_modal.email_label')}</label>
                </Fieldset>
              </FormFieldset>
            )}

            <FormFieldset form={form}>
              <Fieldset>
                <input
                  {...form.register('recipient_email', {
                    ...validation.required(),
                    ...validation.email(),
                  })}
                  type="email"
                  placeholder="&nbsp;"
                  list="contacts_list"
                />
                <datalist id="contacts_list">
                  {myContacts.data?.[0]?.json?.items?.map?.((contact) => {
                    return contact.contact_user?.email ? (
                      <option key={contact.uuid} value={contact.contact_user?.email} />
                    ) : null
                  })}
                </datalist>
                <label>{t('future_message.email_modal.recipient_email_label')}</label>
              </Fieldset>
            </FormFieldset>
          </FormFieldset>

          <SButtonsContainer>
            <Button size="large" variant="secondary" type="button" onClick={props.onClose}>
              {t('future_message.email_modal.cancel_button')}
            </Button>
            <Button type="submit" size="large" loading={form.formState.isSubmitting}>
              {t('future_message.email_modal.submit_button')}
            </Button>
          </SButtonsContainer>
        </form>
      )}
    </SEmailModal>
  )
}

export const useEmailModal = (props: {
  title?: ReactNode
  text?: ReactNode
  preview?: ReactElement
  previewCss?: InterpolationPrimitive
  onSend?: (payload: EmailModalFields) => void
  css?: InterpolationPrimitive
  defaultEmail?: string
}) => {
  const [emailModal, openEmailModal, closeEmailModal] = useModal(
    ({ close, isOpen }) =>
      isOpen ? (
        <EmailModal
          title={props?.title}
          preview={props?.preview}
          previewCss={props.previewCss}
          text={props?.text}
          onSend={props?.onSend}
          defaultEmail={props?.defaultEmail}
          onClose={close}
          css={props.css}
        />
      ) : null,
    {
      modalCss: css`
        @media ${BREAKPOINTS.MD.min} {
          max-width: ${pxToRem(460)}rem;
        }
      `,
    }
  )
  return [emailModal, openEmailModal, closeEmailModal] as const
}
